import type { BlocoLocal, RamoCondicao, RamoPasso, TipoBloco } from '../types/editor';

import { avisaEAguarda, temBlocosNoRamoDeTimeout } from './esperaVencimento';
import { labelDoEvento } from './eventosArbitrais';

export type TomRamo = 'sucesso' | 'neutro' | 'aviso';

export interface RamoDefinicao {
  chave: RamoCondicao;
  rotulo: string;
  tom: TomRamo;
  /** Presente em ramos por evento — sub-chave do Record `aoOcorrerPorEvento`. */
  evento?: string;
}

/**
 * Blocos cujos sub-fluxos são desenhados como árvore (ramos) logo abaixo do
 * bloco no canvas — em qualquer profundidade (ramos aninham). Cada ramo aponta
 * para uma chave de `parametros` que guarda um BlocoLocal[]. O compilador, a
 * clonagem e as validações já tratam essas chaves como sub-blocos — aqui é só
 * a autoria visual.
 */
const RAMOS_POR_TIPO: Partial<Record<TipoBloco, RamoDefinicao[]>> = {
  CONDICAO: [
    { chave: 'seVerdadeiro', rotulo: 'Verdadeiro', tom: 'sucesso' },
    { chave: 'seFalso', rotulo: 'Falso', tom: 'neutro' },
  ],
  AGUARDAR_EVENTO: [
    { chave: 'aoOcorrer', rotulo: 'Quando o evento ocorrer', tom: 'sucesso' },
    { chave: 'aoTimeout', rotulo: 'Quando o timeout vencer', tom: 'aviso' },
  ],
  AGUARDAR_VISUALIZACAO: [
    { chave: 'aoVisualizar', rotulo: 'Quando todos visualizarem', tom: 'sucesso' },
    { chave: 'aoTimeout', rotulo: 'Quando o timeout vencer', tom: 'aviso' },
  ],
  AGUARDAR_CITACAO: [
    { chave: 'aoCitar', rotulo: 'Quando todos forem citados', tom: 'sucesso' },
    { chave: 'aoTimeout', rotulo: 'Quando o timeout vencer', tom: 'aviso' },
  ],
  INICIAR_PRAZO: [{ chave: 'aoVencer', rotulo: 'Ações no vencimento', tom: 'aviso' }],
};

type BlocoComRamos = Pick<BlocoLocal, 'tipo' | 'parametros'>;

function decidePorEvento(bloco: BlocoComRamos): boolean {
  return bloco.tipo === 'AGUARDAR_EVENTO' && bloco.parametros.porEvento === true;
}

/**
 * Ramos do bloco como desenhados no canvas. AGUARDAR_EVENTO com "decidir por
 * evento" ativo ganha um ramo por evento selecionado (chave
 * 'aoOcorrerPorEvento' + sub-chave `evento`) no lugar do ramo único
 * 'aoOcorrer'; o ramo de timeout permanece.
 */
export function ramosDoBloco(bloco: BlocoComRamos): RamoDefinicao[] {
  const ramos = ramosDeclaradosDoBloco(bloco);
  // No modo "avisar e continuar aguardando" o vencimento não sai pelo ramo de timeout. Vazio,
  // ele some; com blocos (fluxo antigo, JSON importado) continua visível até ser esvaziado,
  // senão as ações sumiriam do canvas sem sumir da validação.
  if (avisaEAguarda(bloco) && !temBlocosNoRamoDeTimeout(bloco)) {
    return ramos.filter((ramo) => ramo.chave !== 'aoTimeout');
  }
  return ramos;
}

function ramosDeclaradosDoBloco(bloco: BlocoComRamos): RamoDefinicao[] {
  if (decidePorEvento(bloco)) {
    const eventos = Array.isArray(bloco.parametros.eventos)
      ? (bloco.parametros.eventos as string[])
      : [];
    return [
      ...eventos.map(
        (evento): RamoDefinicao => ({
          chave: 'aoOcorrerPorEvento',
          evento,
          rotulo: labelDoEvento(evento),
          tom: 'sucesso',
        }),
      ),
      { chave: 'aoTimeout', rotulo: 'Quando o timeout vencer', tom: 'aviso' },
    ];
  }
  return RAMOS_POR_TIPO[bloco.tipo] ?? [];
}

/**
 * O passo do caminho (`path`) que leva aos blocos do ramo. `evento` só entra em ramo por evento:
 * as comparações de caminho usam `passo.evento === undefined` para os ramos comuns.
 *
 * @example passoDoRamo({ chave: 'aoOcorrerPorEvento', evento: 'x', rotulo: 'X', tom: 'sucesso' })
 * // { ramo: 'aoOcorrerPorEvento', evento: 'x' }
 */
export function passoDoRamo(ramo: RamoDefinicao): Omit<RamoPasso, 'condId'> {
  return { ramo: ramo.chave, ...(ramo.evento ? { evento: ramo.evento } : {}) };
}

export function temRamosNoCanvas(bloco: BlocoComRamos): boolean {
  return ramosDoBloco(bloco).length > 0;
}

export function rotuloRamo(bloco: BlocoComRamos, passo: Omit<RamoPasso, 'condId'>): string {
  return (
    ramosDoBloco(bloco).find((ramo) => ramo.chave === passo.ramo && ramo.evento === passo.evento)
      ?.rotulo ?? ''
  );
}
